import css from './testPanel.module.scss';

import cx from 'classnames';
import { memo } from 'react';

import { useTestEditor } from 'com/TestEditor';
import { apiDeleteTest } from 'core/api';
import { parseTimeDuration } from 'core/helpers/text';
import { getTestQuestionsUrl, getTestUrl } from 'core/paths';
import { ListPanel } from 'core/ui/ListPanel';
import { MenuItem } from 'core/ui/Menu';
import { useMessageModal } from 'core/ui/MessageModal';
import ICONS, { Icon } from 'core/ui/icons';

type Props = {
  test: TTestRich;
  reload: () => Promise<void>;
};

function TestPanel({ test, reload }: Props) {
  return (
    <ListPanel
      body={{
        type: 'containerPanel',
        title: test.name,
        total: test.questionsCount,
        pending: test.pendingQuestionsCount,
        itemsLabel: 'Questions',
      }}
      actionsWidth="11rem"
      actions={[
        [
          {
            link: getTestUrl(test.uid, 'pending'),
            icon: ICONS.Play,
            title: 'Start test (only pending questions)',
            label: <PendingActionLabel test={test} />,
            disabled: !test.pendingQuestionsCount,
          },
          {
            link: getTestUrl(test.uid),
            icon: ICONS.Play,
            title: 'Start test',
          },
        ],
        [
          {
            icon: ICONS.Cards,
            title: 'Edit questions',
            link: getTestQuestionsUrl(test.uid),
          },
          {
            icon: ICONS.Menu,
            title: 'Show menu',
            menuItems: <MenuBody test={test} reload={reload} />,
          },
        ],
      ]}
    />
  );
}

export default memo(TestPanel);

function PendingActionLabel({ test }: { test: TTestRich }) {
  const duration = test.status.closest
    ? parseTimeDuration(test.status.closest)
    : null;

  return (
    <div
      className={cx(
        css.pendingAction,
        !test.pendingQuestionsCount && css.playBtnInactive
      )}
    >
      <Icon icon="Play" className={css.pendingIcon} />
      <span className={css.pendingCount}>
        {test.status.count ||
          (duration && (
            <>
              {duration.sign}
              {Math.abs(duration.count)}
              {duration.label}
            </>
          ))}
      </span>
    </div>
  );
}

function MenuBody({ test, reload }: Props) {
  const { showError } = useMessageModal();
  const { editTest } = useTestEditor();

  const onDelete = () => {
    if (
      confirm(
        `Are you sure? It's irreversible! The test will be deleted with all its questions!`
      ) &&
      confirm('Once again')
    ) {
      apiDeleteTest(test.uid).then(reload).catch(showError);
    }
  };

  return (
    <>
      <MenuItem
        label="Edit"
        icon={ICONS.Edit}
        onClick={() => editTest(test.uid).then(reload).catch(showError)}
      />
      <MenuItem label="Remove" icon={ICONS.Trash} onClick={onDelete} />
    </>
  );
}
